import { useState } from 'react';
import { Phone, MessageCircle, Puzzle, Globe, BarChart2, Settings, CheckCircle, AlertCircle } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { clsx } from 'clsx';

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  status: 'connected' | 'disconnected' | 'pending';
  color: string;
}

const integrations: Integration[] = [
  {
    id: 'twilio_call',
    name: 'Twilio Voice',
    description: 'Click-to-call, call recordings, IVR setup',
    icon: <Phone size={24} />,
    status: 'connected',
    color: '#F22F46',
  },
  {
    id: 'twilio_whatsapp',
    name: 'WhatsApp (Twilio)',
    description: 'WhatsApp messaging, templates, webhooks',
    icon: <MessageCircle size={24} />,
    status: 'connected',
    color: '#25D366',
  },
  {
    id: 'google_ads',
    name: 'Google Ads',
    description: 'Connect Google Ads to track campaign performance',
    icon: <span className="text-2xl font-bold" style={{ color: '#4285F4' }}>G</span>,
    status: 'disconnected',
    color: '#4285F4',
  },
  {
    id: 'meta_ads',
    name: 'Meta Ads',
    description: 'Connect Facebook & Instagram ad campaigns',
    icon: <span className="text-2xl font-bold" style={{ color: '#1877F2' }}>f</span>,
    status: 'disconnected',
    color: '#1877F2',
  },
  {
    id: 'gmb',
    name: 'Google My Business',
    description: 'Manage reviews, ratings and local SEO',
    icon: <Globe size={24} />,
    status: 'pending',
    color: '#34A853',
  },
  {
    id: 'lead_forms',
    name: 'Lead Form Integration',
    description: 'Connect external lead forms via webhooks/API',
    icon: <Puzzle size={24} />,
    status: 'connected',
    color: '#8B5CF6',
  },
];

export function IntegrationsPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'whatsapp' | 'calls' | 'forms'>('overview');
  const [whatsappSenderId, setWhatsappSenderId] = useState('');
  const [whatsappWebhook, setWhatsappWebhook] = useState('');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Integrations</h1>
        <p className="text-slate-500 text-sm mt-0.5">Manage all external system connections</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
        {(['overview', 'whatsapp', 'calls', 'forms'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize',
              activeTab === tab ? 'bg-white text-brand-navy shadow-sm' : 'text-slate-500 hover:text-slate-700',
            )}
          >
            {tab === 'forms' ? 'Lead Forms' : tab}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {integrations.map(integration => (
            <div key={integration.id} className="bg-white rounded-xl shadow-card border border-slate-100 p-5">
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${integration.color}15`, color: integration.color }}
                >
                  {integration.icon}
                </div>
                <span className={clsx(
                  'px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1',
                  integration.status === 'connected' ? 'bg-green-50 text-green-700' :
                  integration.status === 'pending' ? 'bg-yellow-50 text-yellow-700' :
                  'bg-slate-100 text-slate-500',
                )}>
                  {integration.status === 'connected' ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                  {integration.status}
                </span>
              </div>
              <h3 className="font-semibold text-slate-900">{integration.name}</h3>
              <p className="text-sm text-slate-500 mt-1">{integration.description}</p>
              <button
                className="mt-4 w-full h-8 rounded-lg border border-slate-200 text-sm text-slate-600 hover:border-brand-cyan hover:text-brand-cyan transition-colors flex items-center justify-center gap-1.5"
              >
                <Settings size={14} />
                {integration.status === 'connected' ? 'Configure' : 'Connect'}
              </button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'whatsapp' && (
        <Card title="WhatsApp API Setup" subtitle="Configure Twilio WhatsApp integration">
          <div className="space-y-4 max-w-lg">
            <Input
              label="WhatsApp Sender ID (From Number)"
              value={whatsappSenderId}
              onChange={e => setWhatsappSenderId(e.target.value)}
              placeholder="whatsapp:+14155238886"
            />
            <Input
              label="Webhook URL"
              value={whatsappWebhook}
              onChange={e => setWhatsappWebhook(e.target.value)}
              placeholder="https://your-api.com/api/whatsapp/webhook"
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Message Templates</label>
              <div className="space-y-2">
                {['OPD Confirmation', 'IPD Confirmation', 'Reschedule Notification', 'Review Request', 'Appointment Reminder'].map(t => (
                  <div key={t} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-brand-cyan/50 transition-colors">
                    <span className="text-sm text-slate-700">{t}</span>
                    <div className="flex gap-2">
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Active</span>
                      <button className="text-xs text-slate-400 hover:text-brand-cyan">Edit</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <Button>Save Configuration</Button>
          </div>
        </Card>
      )}

      {activeTab === 'calls' && (
        <Card title="Call Integration Setup" subtitle="Configure Twilio Voice settings">
          <div className="space-y-4 max-w-lg">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                <CheckCircle size={16} />
                Twilio Voice is connected
              </div>
            </div>
            <Input label="Twilio Phone Number" value="+919999999999" readOnly />
            <Input label="TwiML App SID" placeholder="APxxxxxxxxxxxxxx" />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Call Assignment Mode</label>
              <div className="space-y-2">
                {[
                  { id: 'round_robin', label: 'Round Robin', desc: 'Auto-assign to next available BD in rotation' },
                  { id: 'sequential', label: 'Sequential', desc: 'Assign by rank/priority order' },
                ].map(mode => (
                  <label key={mode.id} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:border-brand-cyan/50">
                    <input type="radio" name="callMode" value={mode.id} className="mt-0.5 accent-brand-cyan" />
                    <div>
                      <div className="text-sm font-medium text-slate-900">{mode.label}</div>
                      <div className="text-xs text-slate-500">{mode.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <Button>Save Call Settings</Button>
          </div>
        </Card>
      )}

      {activeTab === 'forms' && (
        <Card title="Lead Form Integration" subtitle="Add external lead form endpoints">
          <div className="space-y-4 max-w-lg">
            <p className="text-sm text-slate-500">
              Connect external lead capture forms (Meta Lead Ads, Google Lead Forms, website forms) via API endpoints or webhooks.
            </p>
            <div className="space-y-3">
              {[
                { source: 'Meta Lead Ads', url: 'https://your-api.com/api/leads/webhook/meta', status: 'active' },
                { source: 'Google Lead Forms', url: 'https://your-api.com/api/leads/webhook/google', status: 'active' },
                { source: 'Website Form', url: 'https://your-api.com/api/leads/webhook/website', status: 'inactive' },
              ].map((form, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
                  <div>
                    <div className="text-sm font-medium text-slate-900">{form.source}</div>
                    <div className="text-xs text-slate-400 font-mono">{form.url}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${form.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {form.status}
                  </span>
                </div>
              ))}
            </div>
            <Button icon={<Puzzle size={14} />} variant="outline">Add New Form Integration</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
